// Inline two-tier facet pills above the product grid (complements the drawer).
// Row 1 = filter dimensions (tabs); row 2 = the values of the active dimension.
// It doesn't own any filter state: it mirrors the Voyado drawer facets and drives
// them by toggling the real drawer inputs, so the grid's existing URL sync + refetch
// (voyado-grid.js #onFacetChange) do all the work. A MutationObserver rebuilds the
// pills whenever the drawer re-renders (first load / filter / sort).

class ApcFacetPills extends HTMLElement {
  #abortController = new AbortController();
  /** @type {MutationObserver | null} */ #observer = null;
  /** @type {Element | null} */ #drawer = null;
  /** id of the dimension whose values are shown in row 2 */ #activeId = null;
  /** icon-close.svg markup, captured from the server-rendered <template>, for the clear (×) controls */ #clearIcon =
    '';

  connectedCallback() {
    const grid = this.closest('voyado-grid');
    this.#drawer = grid?.querySelector('[data-voyado-facets]') ?? null;
    if (!this.#drawer) return;

    // Capture the close-icon markup before the first render replaces our children.
    this.#clearIcon = this.querySelector('template[data-apc-clear-icon]')?.innerHTML ?? '';

    this.addEventListener('click', this.#onClick, { signal: this.#abortController.signal });

    // Rebuild whenever the drawer facets change (they're replaced wholesale on re-render).
    this.#observer = new MutationObserver(() => this.#render());
    this.#observer.observe(this.#drawer, { childList: true, subtree: true });
    this.#render();
  }

  disconnectedCallback() {
    this.#abortController.abort();
    this.#observer?.disconnect();
  }

  /**
   * Read the drawer's facet groups into a plain structure. Range facets (price) have no
   * value inputs and are skipped — pills only make sense for checkbox/swatch dimensions.
   * @returns {Array<{id: string, label: string, active: number, values: Array<{id: string, label: string, count: string, checked: boolean, color: string, input: HTMLInputElement}>}>}
   */
  #readDimensions() {
    const dimensions = [];
    for (const group of this.#drawer?.querySelectorAll('.facets__item') ?? []) {
      const label = group.querySelector('.facets__label')?.textContent?.trim() || '';
      const values = [];
      for (const input of group.querySelectorAll('input[data-voyado-slot="input"]')) {
        if (!(input instanceof HTMLInputElement) || input.type !== 'checkbox' || !input.dataset.facetId) continue;
        const item = input.closest('.facets__inputs-list-item') || input.parentElement;
        const swatch = /** @type {HTMLElement | null} */ (item?.querySelector('[data-voyado-slot="swatch"]'));
        values.push({
          id: input.value,
          label: item?.querySelector('[data-voyado-slot="label"]')?.textContent?.trim() || input.value,
          count: item?.querySelector('[data-apc-facet-count]')?.textContent?.trim() || '',
          checked: input.checked,
          hasSwatch: Boolean(swatch),
          color: swatch?.style.getPropertyValue('--swatch-background') || '',
          input,
        });
      }
      if (!values.length) continue;
      dimensions.push({
        id: values[0].input.dataset.facetId || label,
        label,
        active: values.filter((v) => v.checked).length,
        isSwatch: values.some((v) => v.hasSwatch),
        values,
      });
    }
    return dimensions;
  }

  #render() {
    const dimensions = this.#readDimensions();
    if (!dimensions.length) {
      // Drawer rendered facets but none are pill-able → clear the skeleton and collapse.
      // Still loading (no facet items yet) → leave the server-rendered skeleton in place.
      if (this.#drawer?.querySelector('.facets__item')) {
        this.replaceChildren();
        this.hidden = true;
      }
      return;
    }
    this.hidden = false;

    // Keep the active dimension across rebuilds; fall back to the first.
    if (!dimensions.some((d) => d.id === this.#activeId)) this.#activeId = dimensions[0].id;
    const active = dimensions.find((d) => d.id === this.#activeId) ?? dimensions[0];

    const row1 = document.createElement('div');
    row1.className = 'apc-facet-pills__dimensions';
    row1.setAttribute('role', 'tablist');
    for (const dim of dimensions) {
      const tab = document.createElement('button');
      tab.type = 'button';
      tab.className = 'apc-facet-pills__dim';
      tab.dataset.apcDim = dim.id;
      tab.setAttribute('role', 'tab');
      tab.setAttribute('aria-selected', String(dim.id === active.id));
      tab.textContent = dim.label;
      if (dim.active) {
        const badge = document.createElement('span');
        badge.className = 'apc-facet-pills__dim-count';
        badge.textContent = `(${dim.active})`;
        tab.appendChild(badge);

        // Clear-this-dimension affordance (×) next to the active count.
        const clear = document.createElement('span');
        clear.className = 'apc-facet-pills__dim-clear';
        clear.dataset.apcClearDim = '';
        clear.setAttribute('aria-label', `${this.dataset.clearText || 'Clear'} ${dim.label}`);
        clear.title = clear.getAttribute('aria-label');
        clear.innerHTML = this.#clearIcon;
        tab.appendChild(clear);
      }
      row1.appendChild(tab);
    }

    // Clear-all — reuses the grid's own clear (base #onClearAll listens for [data-voyado-clear-all]).
    if (this.#hasFilters()) {
      const clearAll = document.createElement('button');
      clearAll.type = 'button';
      clearAll.className = 'apc-facet-pills__clear-all';
      clearAll.dataset.voyadoClearAll = '';
      const clearAllLabel = this.dataset.clearAllText || 'Clear all';
      clearAll.setAttribute('aria-label', clearAllLabel);
      clearAll.title = clearAllLabel;
      clearAll.innerHTML = this.#clearIcon;
      row1.appendChild(clearAll);
    }

    const row2 = document.createElement('div');
    row2.className = 'apc-facet-pills__values';
    if (active.isSwatch) row2.classList.add('apc-facet-pills__values--swatches');
    row2.setAttribute('role', 'tabpanel');
    for (const value of active.values) {
      const pill = document.createElement('button');
      pill.type = 'button';
      pill.dataset.apcValue = value.id;
      pill.setAttribute('aria-pressed', String(value.checked));
      // Store the live drawer input so the click just proxies to it.
      /** @type {any} */ (pill).apcInput = value.input;

      const count = document.createElement('span');
      count.className = 'apc-facet-pills__value-count';
      count.textContent = value.count;

      if (active.isSwatch) {
        // Colour dimension: show the swatch only (name via aria-label), like the drawer.
        pill.className = 'apc-facet-pills__value apc-facet-pills__value--swatch';
        pill.setAttribute('aria-label', value.count ? `${value.label} ${value.count}` : value.label);
        pill.title = value.label;
        // Reuse the drawer's swatch classes so size/shape/colour match exactly.
        const dot = document.createElement('span');
        dot.className = 'swatch swatch--filter';
        if (value.color) dot.style.setProperty('--swatch-background', value.color);
        else dot.classList.add('swatch--unavailable');
        pill.appendChild(dot);
        if (value.count) pill.appendChild(count);
      } else {
        pill.className = 'apc-facet-pills__value';
        pill.appendChild(document.createTextNode(value.label));
        if (value.count) pill.appendChild(count);
      }
      row2.appendChild(pill);
    }

    this.replaceChildren(row1, row2);
  }

  /** Whether any facet filter (f.*) is active in the URL — includes ranges like price. */
  #hasFilters() {
    return [...new URLSearchParams(location.search).keys()].some((key) => key.startsWith('f.'));
  }

  /**
   * Clear all selected values of one dimension by unchecking its drawer inputs and firing a
   * single change, so the grid's own #onFacetChange drops f.<facetId> and refetches.
   * @param {string} facetId
   */
  #clearDimension(facetId) {
    const inputs = this.#drawer?.querySelectorAll(`input[type="checkbox"][data-facet-id="${CSS.escape(facetId)}"]`);
    if (!inputs?.length) return;
    /** @type {HTMLInputElement | null} */
    let touched = null;
    for (const input of inputs) {
      if (/** @type {HTMLInputElement} */ (input).checked) {
        /** @type {HTMLInputElement} */ (input).checked = false;
        touched = /** @type {HTMLInputElement} */ (input);
      }
    }
    (touched ?? /** @type {HTMLInputElement} */ (inputs[0])).dispatchEvent(new Event('change', { bubbles: true }));
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);

    // Per-dimension clear (×) — clear that dimension without switching the active tab.
    const clearDim = target.closest('[data-apc-clear-dim]');
    if (clearDim instanceof HTMLElement) {
      event.stopPropagation();
      const facetId = /** @type {HTMLElement | null} */ (clearDim.closest('[data-apc-dim]'))?.dataset.apcDim;
      if (facetId) this.#clearDimension(facetId);
      return;
    }

    const tab = target.closest('.apc-facet-pills__dim');
    if (tab instanceof HTMLElement) {
      this.#activeId = tab.dataset.apcDim || null;
      this.#render();
      return;
    }

    const pill = target.closest('.apc-facet-pills__value');
    if (pill instanceof HTMLElement) {
      const input = /** @type {HTMLInputElement | undefined} */ (/** @type {any} */ (pill).apcInput);
      if (!input) return;
      // Flip the real drawer checkbox and let the grid's own change handler apply it.
      input.checked = !input.checked;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  };
}

if (!customElements.get('apc-facet-pills')) {
  customElements.define('apc-facet-pills', ApcFacetPills);
}
